// client/src/components/analytics/AnalyticsHistoryPanel.jsx
//
// Long-range history on the Analytics page (Roadmap Phase 2.1): daily
// impressions / engagement / clicks from the stored daily snapshots, and
// followers per platform. The server clamps the range to the plan's
// analytics_history_days and says so in `window`; this shows that limit.

import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  ResponsiveContainer, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
} from 'recharts';
import { fetchAnalyticsHistory, formatMetricNumber, PLATFORM_META } from '../../services/analyticsApi';
import './PerformancePanel.css';

const RANGES = [90, 180, 365, 730];

/** Followers rows → [{ date, facebook: n, instagram: n, ... }] and the platforms seen. */
export function followerSeries(rows) {
  const byDate = new Map();
  const platforms = new Set();
  for (const r of rows || []) {
    platforms.add(r.platform);
    if (!byDate.has(r.date)) byDate.set(r.date, { date: r.date });
    byDate.get(r.date)[r.platform] = r.followers;
  }
  return { data: [...byDate.values()].sort((a, b) => a.date.localeCompare(b.date)), platforms: [...platforms] };
}

export default function AnalyticsHistoryPanel({ tenantId }) {
  const { t } = useTranslation();
  const [days, setDays] = useState(365);
  const [data, setData] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;
    setError('');
    fetchAnalyticsHistory(tenantId, { days })
      .then((r) => { if (!cancelled) setData(r); })
      .catch(() => { if (!cancelled) setError(t('analytics.history.error', 'Could not load history.')); });
    return () => { cancelled = true; };
  }, [tenantId, days, t]);

  const totals = (data && data.series || []).reduce((acc, d) => ({
    impressions: acc.impressions + d.impressions, engagement: acc.engagement + d.engagement, clicks: acc.clicks + d.clicks,
  }), { impressions: 0, engagement: 0, clicks: 0 });
  const followers = followerSeries(data && data.followers);
  const w = data && data.window;

  return (
    <section className="surface perf-panel" aria-labelledby="an-history-title" data-testid="analytics-history" style={{ marginBottom: 16 }}>
      <div className="perf-head" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
        <h4 id="an-history-title" style={{ margin: 0 }}>{t('analytics.history.title', 'History')}</h4>
        <label className="perf-small" htmlFor="an-history-range">
          {t('analytics.history.range', 'Show')}{' '}
          <select id="an-history-range" value={days} onChange={(e) => setDays(Number(e.target.value))}>
            {RANGES.map((d) => <option key={d} value={d}>{t('analytics.history.days', 'Last {{count}} days', { count: d })}</option>)}
          </select>
        </label>
      </div>
      <p className="surface-muted perf-small" style={{ marginTop: 4 }}>
        {t('analytics.history.intro', 'On-Screen saves a snapshot of every post and account each day, so you keep your history even after the platforms stop reporting older posts.')}
        {w && w.limitDays ? ` ${t('analytics.history.planLimit', 'Your plan keeps {{count}} days.', { count: w.limitDays })}` : ''}
        {w && w.clamped ? ` ${t('analytics.history.clamped', 'Showing the last {{count}} days; upgrade in Billing for more.', { count: w.days })}` : ''}
      </p>
      {error && <div className="note error" role="alert">{error}</div>}
      {data && data.migrationPending && (
        <div className="note info">{t('analytics.history.pending', 'History starts once a database update is applied.')}</div>
      )}
      {data && !data.migrationPending && data.series.length === 0 && (
        <div className="note info">{t('analytics.history.empty', 'No history yet. It builds up day by day once posts are published to connected accounts.')}</div>
      )}
      {data && data.series.length > 0 && (
        <>
          <div className="perf-kpis">
            <div className="perf-kpi"><div className="perf-kpi-value">{formatMetricNumber(totals.impressions)}</div><div className="perf-kpi-label">{t('analytics.history.impressions', 'Impressions')}</div></div>
            <div className="perf-kpi"><div className="perf-kpi-value">{formatMetricNumber(totals.engagement)}</div><div className="perf-kpi-label">{t('analytics.history.engagement', 'Engagement')}</div></div>
            <div className="perf-kpi"><div className="perf-kpi-value">{formatMetricNumber(totals.clicks)}</div><div className="perf-kpi-label">{t('analytics.history.clicks', 'Clicks (platform)')}</div></div>
          </div>
          <div style={{ width: '100%', height: 220 }} role="img" aria-label={t('analytics.history.chartLabel', 'Daily impressions and engagement')}>
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={data.series} margin={{ top: 8, right: 12, bottom: 8, left: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                <XAxis dataKey="date" tick={{ fontSize: 11, fill: '#4b5563' }} interval="preserveStartEnd" />
                <YAxis tick={{ fontSize: 11, fill: '#4b5563' }} width={48} />
                <Tooltip />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                <Line type="monotone" dataKey="impressions" name={t('analytics.history.impressions', 'Impressions')} stroke="#253866" dot={false} isAnimationActive={false} />
                <Line type="monotone" dataKey="engagement" name={t('analytics.history.engagement', 'Engagement')} stroke="#c94f00" dot={false} isAnimationActive={false} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </>
      )}
      {followers.data.length > 1 && (
        <div style={{ width: '100%', height: 200 }} role="img" aria-label={t('analytics.history.followersLabel', 'Followers per platform')}>
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={followers.data} margin={{ top: 8, right: 12, bottom: 8, left: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
              <XAxis dataKey="date" tick={{ fontSize: 11, fill: '#4b5563' }} interval="preserveStartEnd" />
              <YAxis tick={{ fontSize: 11, fill: '#4b5563' }} width={48} />
              <Tooltip />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              {followers.platforms.map((p) => (
                <Line key={p} type="monotone" dataKey={p} name={(PLATFORM_META[p] || {}).name || p} stroke={(PLATFORM_META[p] || {}).color || '#253866'} dot={false} isAnimationActive={false} connectNulls />
              ))}
            </LineChart>
          </ResponsiveContainer>
        </div>
      )}
      <p className="perf-small" style={{ margin: '8px 0 0' }}>
        <Link to={`/${tenantId}/reports`}>{t('analytics.history.reportsLink', 'Build a report or a board update from this history →')}</Link>
      </p>
    </section>
  );
}
